import React, { useEffect } from 'react';
import { Lock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { SUPPORTED_CURRENCIES } from '@/api/currency';
import useSubscription from '@/contexts/SubscriptionContext';
import { canSelectCurrency, isPremium } from '@/lib/featureGates';

interface CurrencyDropdownProps {
  value?: string;
  onValueChange: (value: string) => void;
  width?: string;
  className?: string;
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({
  value,
  onValueChange,
  width = 'w-24',
  className = 'bg-card',
}) => {
  const { preferences } = useUserPreferences();
  const { status } = useSubscription();
  const defaultCurrency = preferences?.base_currency || 'TRY';
  const currentValue = value || defaultCurrency;

  // Set default value from user preferences when component mounts
  useEffect(() => {
    if (preferences?.base_currency) {
      onValueChange(preferences.base_currency);
    }
  }, [preferences]);

  // Helper function to get currency symbol
  const getCurrencySymbol = (val: string) => {
    const currency = SUPPORTED_CURRENCIES.find(c => c.value === val);
    return currency ? currency.symbol : val;
  };

  return (
    <Select
      value={currentValue}
      onValueChange={onValueChange}
      defaultValue={defaultCurrency}
    >
      <SelectTrigger className={`${width} ${className}`}>
        <SelectValue placeholder={getCurrencySymbol(defaultCurrency)} />
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_CURRENCIES.map((currency) => {
          const allowed = canSelectCurrency(currency.value, status, currentValue);
          return (
            <SelectItem
              key={currency.value}
              value={currency.value}
              disabled={!allowed}
              title={!allowed && !isPremium(status) ? 'Premium gerekli' : undefined}
            >
              <span className="flex items-center gap-2">
                <span>{currency.label} ({currency.symbol})</span>
                {!allowed && <Lock className="h-3 w-3 opacity-60" />}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

export default CurrencyDropdown;
